<template lang="pug">
.uploader(v-if="currentFiles")
  .summary(:class="{'border': showFileList}")
    .progress-bg(:style="{ width: `${totalPercent}%`}", :class="{'done': totalPercent === 100}" v-if="!showFileList")
    .text
      template(v-if="currentFiles > 0")
        template(v-if="currentFilesState.uploading > 0")
          h3 Uploading {{ currentFilesState.uploading }} {{ currentFilesState.uploading === 1 ? 'file' : 'files' }}
        template(v-else-if="currentFilesState.error > 0")
          h3.error {{ currentFilesState.error }} {{ currentFilesState.error === 1 ? 'upload' : 'uploads' }} failed
        template(v-else-if="currentFilesState.uploaded > 0")
          h3 {{ currentFilesState.uploaded }} Uploaded
    .buttons
      i(:class="{'mdi-arrow-expand': !showFileList, 'mdi-arrow-collapse': showFileList}", @click="showFileList = !showFileList").mdi
      i.mdi.mdi-close-circle-outline.abort-all(@click="abortAndRemoveAll")
  .uploads(v-if="showFileList")
    .upload(v-for="(file, index) in files", :key="index")
      .progress-bg(:style="{ width: `${file.progress}%`}", :class="{'done': file.progress === 100, 'error': file.state === 'error'}")
      .title {{ file.name }}
      .progress
        template(v-if="file.state === 'uploading'")
          span {{ file.progress }}%
        template(v-else-if="file.state === 'uploaded'")
          i.mdi.mdi-check.file-success
        template(v-else-if="file.state === 'error'")
          i.mdi.mdi-alert-circle-outline.file-error
      .actions
        i.mdi.mdi-close-circle-outline.abort(@click="abortAndRemove(index)")
</template>

<script>
// Origin of the joshua backend's signed-URL upload endpoints
// (POST /uploads, POST /uploads/{videoId}/complete). Falls back to the
// shared VUE_APP_API_URL (already pointed at joshua for this rollout);
// the deploy task can pin this to a dedicated value if it ever needs to
// diverge from the rest of the API.
const JOSHUA_API = process.env.VUE_APP_API_URL;

export default {
  computed: {
    /**
     * Return the number of total uploads
     * @return {Number}
     */
    uploads() {
      // eslint-disable-next-line no-unused-vars
      const { xhrs, timestamp } = this;
      return Object.keys(xhrs).length;
    },
    /**
     * Return the number of total files in list
     * @return {Number}
     */
    currentFiles() {
      // eslint-disable-next-line no-unused-vars
      const { files, timestamp } = this;
      return Object.keys(files).length;
    },
    /**
     * Return the number of total states in the files list
     * @return {Object}
     */
    currentFilesState() {
      // eslint-disable-next-line no-unused-vars
      const { files, timestamp } = this;
      const states = {
        uploaded: 0,
        error: 0,
        uploading: 0,
      };
      const keys = Object.keys(files);
      for (let i = 0; i < keys.length; i += 1) {
        if (files[keys[i]].state === 'uploading') {
          states.uploading += 1;
        }
        if (files[keys[i]].state === 'error') {
          states.error += 1;
        }
        if (files[keys[i]].state === 'uploaded') {
          states.uploaded += 1;
        }
      }
      return states;
    },
    /**
     * Return the total percentage of all files combines
     * @return {Number}
     */
    totalPercent() {
      // eslint-disable-next-line no-unused-vars
      const { xhrs, files, timestamp } = this;
      let sumPercent = 0;
      const keys = Object.keys(xhrs);
      for (let i = 0; i < keys.length; i += 1) {
        if (files[keys[i]].state !== 'error') {
          sumPercent += files[keys[i]].progress;
        }
      }
      return sumPercent / Object.keys(xhrs).length;
    },
  },
  methods: {
    /**
     * Add event to notify user that upload is ongoing when closing the page
     */
    addConfirm() {
      window.addEventListener('beforeunload', this.beforeunloadFunc);
    },
    /**
     * Remove the notify event
     */
    removeConfirm() {
      window.removeEventListener('beforeunload', this.beforeunloadFunc);
    },
    /**
     * Prevent closing
     * @param {Object} event
     */
    beforeunloadFunc(event) {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    },
    /**
     * Check if any of the current files is still uploading
     * @return {Boolean}
     */
    checkUploads() {
      const { files } = this;
      const keys = Object.keys(files);
      let isUploading = false;
      for (let i = 0; i < keys.length; i += 1) {
        if (files[keys[i]].state === 'uploading') {
          isUploading = true;
          this.$store.dispatch('Upload/uploading', true);
          this.$store.commit('Upload/uploadingKey');
        }
      }
      if (!isUploading) {
        this.removeConfirm();
        this.$store.dispatch('Upload/uploading', false);
        this.$store.commit('Upload/uploadingKey');
      }
      return isUploading;
    },
    /**
     * Abort and remove file from list
     * @param {String} index
     */
    async abortAndRemove(index) {
      if (this.files[index].state === 'uploading') {
        // eslint-disable-next-line
        const willAbort = await swal({
          title: this.$t('common.are_you_sure'),
          text: this.$t('video.upload_remove'),
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        });
        if (willAbort) {
          this.xhrs[index].abort();
          delete this.xhrs[index];
          delete this.files[index];
        }
      } else {
        delete this.xhrs[index];
        delete this.files[index];
      }
      this.timestamp = new Date().getTime();
      this.checkUploads();
    },
    /**
     * Abort and remove all files from list
     */
    async abortAndRemoveAll() {
      const { files } = this;
      const keys = Object.keys(files);
      const isUploading = this.checkUploads();
      if (isUploading) {
        // eslint-disable-next-line
        const willAbort = await swal({
          title: this.$t('common.are_you_sure'),
          text: this.$t('video.upload_refresh'),
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        });
        if (!willAbort) {
          return;
        }
      }
      for (let i = 0; i < keys.length; i += 1) {
        if (this.files[keys[i]].state === 'uploading') {
          this.xhrs[keys[i]].abort();
        }
        if (this.xhrs[keys[i]]) {
          delete this.xhrs[keys[i]];
        }
        delete this.files[keys[i]];
      }

      this.timestamp = new Date().getTime();
      this.checkUploads();
    },
    /**
     * Add a file to the upload list
     * @param {Object} file
     */
    async addFile(file) {
      const id = Math.random().toString(36).substr(2, 9);
      this.files[id] = {
        name: file.name,
      };
      this.addConfirm();

      const result = await (async () => {
        const { data: mint } = await ovpFetch(`${JOSHUA_API}/uploads`, {
          method: 'POST',
          body: JSON.stringify({ filename: file.name, contentType: file.type }),
        });

        await xhrPut(mint.upload_url, file, (percent) => {
          if (this.files[id]) {
            this.files[id].state = 'uploading';
            this.files[id].progress = Math.round(percent);
            this.timestamp = new Date().getTime();
          }

          this.checkUploads();
        }, (xhr) => {
          this.xhrs[id] = xhr;
          this.timestamp = new Date().getTime();
        });

        // video_id is a STRING (64-bit snowflake) — used verbatim in the URL; a
        // JS number would lose precision above 2^53 and 404 the /complete lookup.
        return ovpFetch(`${JOSHUA_API}/uploads/${mint.video_id}/complete`, {
          method: 'POST',
        });
      })().catch(() => false);

      if (!result) {
        if (this.files[id]) {
          if (this.files[id].state !== 'aborted') {
            delete this.xhrs[id];
            this.files[id].state = 'error';
          }
        }
        this.timestamp = new Date().getTime();
        this.checkUploads();
        return;
      }

      if (this.files[id]) {
        if (result.data.status.life_cycle_status === 'uploadingComplete') {
          delete this.xhrs[id];
          this.files[id].state = 'uploaded';
          this.timestamp = new Date().getTime();
          this.checkUploads();
        }
      }
    },
    /**
     * Abort or close a uploaded file
     * @param {Number} index
     */
    closeUpload(id) {
      if (this.files[id].state === 'uploading') {
        // Swal
        this.xhrs[id].abort();
        delete this.xhrs[id];
        delete this.files[id];
      } else {
        delete this.files[id];
      }
      this.timestamp = new Date().getTime();
    },
  },
  data() {
    return {
      xhrs: {},
      files: {},
      showFileList: false,
      timestamp: '',
    };
  },
  mounted() {
    this.$root.$on('fileUpload', this.addFile);
  },
  beforeDestroy() {
    this.$root.$off('fileUpload', this.addFile);
  },
};
</script>

<style lang="sass" scoped>
.uploader
  box-shadow: 2px 2px 20px 1px rgba(0, 0, 0, .1)
  border-radius: 5px
  border: 1px solid #EBEEF0
  display: flex
  flex-direction: column
  position: fixed
  right: 2rem
  bottom: 2rem
  z-index: 100
  width: 100%
  background-color: white
  overflow: hidden
  max-width: calc(100vw - 4rem)
  @media (min-width: 600px)
    max-width: 350px
  .summary
    align-items: center
    display: flex
    position: relative
    justify-content: center
    padding: 1rem
    &.border
      border-bottom: 1px solid #EBEEF0
    h3
      font-weight: 500
      &.error
        color: #D0021B
    p
      margin-top: .5rem
    i
      color: #aaaaaa
      font-size: 1.2rem
      &:hover
        color: black
      &.abort-all
        font-size: 1.4rem
        color: #aaaaaa
        &:hover
          color: #D0021B
    .buttons
      display: flex
      margin-left: auto
      & > *
        margin-left: .5rem
        &:first-child
          margin-left: 0
  .upload
    align-items: center
    justify-content: center
    background-color: white
    border-top: 1px solid #EBEEF0
    display: flex
    padding: .5rem 1rem
    width: 100%
    position: relative
    &:first-child
      border-top: 0
    .title
      white-space: nowrap
      overflow: hidden
      text-overflow: ellipsis
      font-size: .8rem
      margin-right: 1rem
      position: relative
    .actions
      position: relative
      .abort
        color: #aaaaaa
        &:hover
          color: #D0021B
    .progress
      font-size: .7rem
      margin-left: auto
      margin-right: .5rem
      i
        font-size: 1rem
        &.file-error
          color: #D0021B
        &.file-success
          color: #12720e
  .uploads
    max-height: 50vh
    overflow: auto
i
  &:hover
    cursor: pointer
.progress-bg
  background-color: rgba(#0695f3, .1)
  bottom: 0
  left: 0
  position: absolute
  top: 0
  pointer-events: none
  z-index: 0
  &.done
    background-color: rgba(#21ff19, .2)
  &.error
    background-color: rgba(#D0021B, .2)
</style>
